(() => {
  "use strict";

  if (window.__cfCollectionUI) return;
  window.__cfCollectionUI = true;

  const ACTIVE_FACET_SELECTORS = [
    "#FacetFiltersActive",
    "[data-active-facets]",
  ];

  const COMING_SOON_POLICY = "all";
  const THEME_BUTTON_PARITY = true;
  const APPEND_PAGINATION_MODES = ["infinite", "click"];
  const AUTO_LOAD_INFINITE = true;
  const WATCH_GRID_RERENDERS = true;
  const USE_THEME_RENDERER = true;
  const TAKE_OVER_INFINITE_AUTOLOAD = true;
  const REMOVE_DUPLICATE_PROMOS = true;

  let initialized = false;
  let searchInitialized = false;

  const normalizeText = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/gi, "")
      .trim();

  const getFilterWrap = () =>
    document.querySelector(".inspired-by-filter-wrap");

  const getFilterPanel = (wrap) =>
    wrap?.querySelector(".inner-panel") || null;

  const getInspiredParam = (panel) =>
    panel?.dataset.inspiredParam ||
    panel?.getAttribute("data-inspired-param") ||
    null;

  const getInputs = (panel) =>
    panel
      ? Array.from(panel.querySelectorAll("input[data-inspired-option]"))
      : [];

  const getMatchingValues = (input) =>
    (input.getAttribute("data-matching-values") || "")
      .split("::")
      .map((value) => value.trim())
      .filter(Boolean);

  const getBrand = (input) =>
    input.getAttribute("data-brand") || input.value;

  const getActiveFacetContainer = () => {
    for (const selector of ACTIVE_FACET_SELECTORS) {
      const element = document.querySelector(selector);
      if (element) return element;
    }
    return null;
  };

  const getRemovedValues = (url, paramName, currentUrl = new URL(location.href)) => {
    try {
      const currentValues = currentUrl.searchParams.getAll(paramName);
      const targetValues = url.searchParams.getAll(paramName);
      return currentValues.filter(
        (value) => !targetValues.includes(value)
      );
    } catch {
      return [];
    }
  };

  const productListQuery = (url) => {
    try {
      const params = new URL(url.toString(), location.origin).searchParams;
      params.delete("inspired_brands");
      params.delete("page");
      params.delete("section_id");
      return Array.from(params.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, value]) => `${key}=${value}`)
        .join("&");
    } catch {
      return "";
    }
  };

  const collectSelection = (panel) => {
    const checkedInputs = getInputs(panel).filter((input) => input.checked);
    const values = [];
    const brands = [];
    const skipped = [];
    const comingSoon = [];

    checkedInputs.forEach((input) => {
      const matchingValues = getMatchingValues(input);
      const brand = getBrand(input);

      if (
        input.hasAttribute("data-cf-brand") &&
        input.hasAttribute("data-cf-coming-soon")
      ) {
        brands.push(brand);
        comingSoon.push(brand);
        return;
      }

      if (matchingValues.length) {
        brands.push(brand);
        matchingValues.forEach((value) => {
          if (!values.includes(value)) {
            values.push(value);
          }
        });
        return;
      }

      if (input.hasAttribute("data-cf-brand")) {
        brands.push(brand);
        if (input.hasAttribute("data-cf-coming-soon")) {
          comingSoon.push(brand);
        } else {
          skipped.push(brand);
        }
        return;
      }

      values.push(input.value);
      brands.push(brand);
    });

    return {
      paramName: getInspiredParam(panel) ||
        "filter.p.m.custom.inspiration",
      brands,
      values,
      skipped,
      comingSoon,
    };
  };

  const buildUrlFromSelection = (panel, inspirationValues = null) => {
    const selection = collectSelection(panel);
    const url = new URL(location.href);

    url.searchParams.delete(selection.paramName);
    url.searchParams.delete("inspired_brands");
    url.searchParams.delete("page");

    (inspirationValues || selection.values).forEach((value) => {
      url.searchParams.append(selection.paramName, value);
    });

    selection.brands.forEach((brand) => {
      url.searchParams.append("inspired_brands", brand);
    });

    if (selection.skipped.length && inspirationValues === null) {
      console.warn(
        "[Inspired by] Skipped brand(s) with no matching filter values:",
        selection.skipped.join(", ")
      );
    }

    return url;
  };

  const getProductGridContainer = () =>
    document.getElementById("ProductGridContainer");

  const getSectionElement = () =>
    getProductGridContainer()?.closest('[id^="shopify-section-"]') ||
    document.querySelector('[id^="shopify-section-"].collection-section') ||
    document.querySelector(".collection");

  const getSectionId = () => {
    const host = getSectionElement();
    if (host?.id) {
      return host.id.replace(/^shopify-section-/, "");
    }
    const form = document.querySelector("form[is='facet-form']");
    return form?.getAttribute("data-section-id") || null;
  };

  const getCollectionEl = () =>
    document.querySelector("[data-cf-collection]");

  const getClearSelectionUrl = () => {
    const url = getCollectionUrl();
    url.searchParams.delete("inspired_brands");
    url.searchParams.delete("page");
    return url;
  };

  const getCollectionUrl = () => {
    const declared = getCollectionEl()?.getAttribute("data-collection-url");
    if (declared) {
      try {
        return new URL(declared, location.origin);
      } catch {
      }
    }
    return new URL(location.pathname, location.origin);
  };

  const fetchSectionHtml = async (url, sectionId) => {
    const requestUrl = new URL(url.toString(), location.origin);
    if (sectionId) {
      requestUrl.searchParams.set("section_id", sectionId);
    }
    const response = await fetch(requestUrl.toString(), {
      headers: { "X-Requested-With": "XMLHttpRequest" },
      credentials: "same-origin",
    });
    if (!response.ok) {
      throw new Error(`Section request failed: ${response.status}`);
    }
    return response.text();
  };

  const parseHtml = (html) => new DOMParser().parseFromString(html, "text/html");

  const swapSection = (sectionId, doc) => {
    const current = document.getElementById(`shopify-section-${sectionId}`);
    if (!current) return false;
    const fresh =
      doc.getElementById(`shopify-section-${sectionId}`) ||
      doc.querySelector(".collection-section");
    if (!fresh) return false;
    current.innerHTML = fresh.innerHTML;
    return true;
  };

  const getSectionHost = () =>
    getSectionElement() ||
    document.querySelector(".collection");

  const THEME_HOVER_ELEMENTS = { a: "hover-link", button: "hover-button" };
  const THEME_BUTTON_CLASSES = "button button--primary button--md";

  const NON_HOVER_IS_VALUES = new Set([
    "infinite-button",
    "facet-remove",
    "filters-toggle",
    "model-view",
  ]);

  const isHoverCustomElement = (value) =>
    value === "hover-link" || value === "hover-button";

  const getThemeElementName = (tag) => {
    const known = THEME_HOVER_ELEMENTS[tag];
    if (known && typeof customElements !== "undefined" && customElements.get(known)) {
      return known;
    }
    const themed = Array.from(document.querySelectorAll(tag + "[is]")).find(
      (element) =>
        element.classList.contains("button") &&
        isHoverCustomElement(element.getAttribute("is"))
    );
    return themed?.getAttribute("is") || known || null;
  };

  const createThemeElement = (tag) => {
    const name = getThemeElementName(tag);
    if (!name || typeof customElements === "undefined") return null;
    const ctor = customElements.get(name);
    if (!ctor) return null;
    let element;
    try {
      element = document.createElement(tag, { is: name });
    } catch (error) {
      return null;
    }
    if (!(element instanceof ctor)) return null;
    element.setAttribute("is", name);
    return element;
  };

  const BUTTON_TEMPLATE_SELECTOR = "[data-cf-button-template]";

  const getButtonTemplate = () => {
    const host = document.querySelector(BUTTON_TEMPLATE_SELECTOR);
    if (!host) return null;
    const source =
      host.tagName === "TEMPLATE"
        ? host.content?.firstElementChild
        : host.firstElementChild;
    return source ? source.cloneNode(true) : null;
  };

  const setButtonLabel = (button, label) => {
    const textSpan = button.querySelector(".btn-text");
    const text = String(label ?? "").trim();
    if (!textSpan || !text) return;
    textSpan.setAttribute("data-text", text);
    const textNode = Array.from(textSpan.childNodes).find(
      (node) => node.nodeType === 3 && node.textContent.trim()
    );
    if (textNode) {
      textNode.textContent = text;
      return;
    }
    if (!textSpan.querySelector("svg, img, i, .icon")) {
      textSpan.textContent = text;
      return;
    }
    textSpan.insertBefore(document.createTextNode(text + " "), textSpan.firstChild);
  };

  const adoptThemeButton = (element, { force = false } = {}) => {
    if (!element) return element;
    if (!element.classList.contains("button") && !force) return element;
    const currentIs = element.getAttribute("is");
    if (currentIs && isHoverCustomElement(currentIs) && !force) {
      return element;
    }
    if (currentIs && !force && !NON_HOVER_IS_VALUES.has(currentIs)) {
      return element;
    }
    if (
      currentIs &&
      isHoverCustomElement(currentIs) &&
      element.querySelector(".btn-fill") &&
      element.querySelector(".btn-text")
    ) {
      return element;
    }
    const tag = element.tagName.toLowerCase() === "button" ? "button" : "a";
    const upgraded = createThemeElement(tag);
    if (!upgraded) return element;
    Array.from(element.attributes).forEach((attribute) => {
      if (attribute.name === "is") return;
      if (attribute.name === "mode") return;
      upgraded.setAttribute(attribute.name, attribute.value);
    });
    while (element.firstChild) upgraded.appendChild(element.firstChild);
    element.replaceWith(upgraded);
    return upgraded;
  };

  let themeButtonTraits = null;

  const getThemeButtonTraits = () => {
    if (themeButtonTraits) return themeButtonTraits;
    if (!THEME_BUTTON_PARITY) {
      themeButtonTraits = { fill: false, is: null, classes: "" };
      return themeButtonTraits;
    }
    const sample =
      document.querySelector(
        "a.button[is='hover-link'], button.button[is='hover-button'], " +
          ".facet-topbar .button[is], .pagination .button"
      ) || document.querySelector("a.button[is], .button");
    themeButtonTraits = {
      fill: sample ? Boolean(sample.querySelector(".btn-fill, [data-fill]")) : true,
      is: sample?.getAttribute("is") || getThemeElementName("a"),
      classes: sample?.className || THEME_BUTTON_CLASSES,
    };
    return themeButtonTraits;
  };

  const buildThemeButton = (label, href) => {
    const clone = getButtonTemplate();
    const element = clone || createThemeElement("a") || document.createElement("a");
    if (!clone) {
      element.className = THEME_BUTTON_CLASSES;
      if (!element.querySelector(".btn-fill")) {
        element.insertAdjacentHTML(
          "afterbegin",
          '<span class="btn-fill" data-fill></span>'
        );
      }
    }
    if (!element.querySelector(".btn-text")) {
      element.insertAdjacentHTML(
        "beforeend",
        '<span class="btn-text" data-text></span>'
      );
    }
    setButtonLabel(element, label);
    if (href) element.setAttribute("href", href);
    element.removeAttribute("data-cf-button-template");
    return element;
  };

  const ensureThemedButton = (element, label, { force = false } = {}) => {
    if (!element || !THEME_BUTTON_PARITY) return element;
    const button = adoptThemeButton(element, { force });
    const textSpan = button.querySelector(".btn-text");
    if (!textSpan) {
      const wrapper = document.createElement("span");
      wrapper.className = "btn-text";
      while (button.firstChild) {
        if (
          button.firstChild.nodeType === 1 &&
          button.firstChild.classList?.contains("btn-fill")
        ) {
          break;
        }
        wrapper.appendChild(button.firstChild);
      }
      while (button.childNodes.length) {
        const child = button.childNodes[button.childNodes.length - 1];
        if (child.nodeType === 1 && child.classList?.contains("btn-fill")) break;
        if (child === wrapper) break;
        break;
      }
      if (!wrapper.childNodes.length) {
        const snapshot = (label || button.textContent || "").trim();
        Array.from(button.childNodes).forEach((node) => {
          if (node.nodeType === 3) node.remove();
        });
        if (snapshot) wrapper.textContent = snapshot;
      }
      button.appendChild(wrapper);
    }
    if (label) {
      setButtonLabel(button, label);
    } else {
      const span = button.querySelector(".btn-text");
      if (span && !span.getAttribute("data-text")) {
        const text = span.textContent.trim();
        if (text) span.setAttribute("data-text", text);
      }
    }
    if (!button.querySelector(".btn-fill") && getThemeButtonTraits().fill) {
      const fill = document.createElement("span");
      fill.className = "btn-fill";
      fill.setAttribute("data-fill", "");
      button.prepend(fill);
    }
    return button;
  };

  const LOAD_MORE_ATTR = "data-cf-load-more";

  const readTriggerLabel = (trigger) => {
    if (!trigger) return "Show more";
    const labeled =
      trigger.querySelector(".btn-text")?.textContent ||
      trigger.getAttribute("aria-label") ||
      trigger.textContent ||
      "";
    const cleaned = labeled.replace(/\s+/g, " ").trim();
    return cleaned || "Show more";
  };

  const readTriggerHref = (trigger) =>
    trigger?.getAttribute?.("action") ||
    trigger?.getAttribute?.("href") ||
    trigger?.getAttribute?.("data-url") ||
    trigger?.getAttribute?.("data-href") ||
    "";

  const ensureLoadMoreButton = (trigger) => {
    if (!trigger || !THEME_BUTTON_PARITY) return trigger;
    const label = readTriggerLabel(trigger);
    const href = readTriggerHref(trigger);
    const currentIs = trigger.getAttribute("is");
    const alreadyHover = isHoverCustomElement(currentIs);
    const hasStructure =
      Boolean(trigger.querySelector(".btn-fill, [data-fill]")) &&
      Boolean(trigger.querySelector(".btn-text"));

    if (alreadyHover && hasStructure) {
      trigger.setAttribute(LOAD_MORE_ATTR, "");
      trigger.removeAttribute("mode");
      const textSpan = trigger.querySelector(".btn-text");
      if (textSpan && !textSpan.getAttribute("data-text")) {
        const text = textSpan.textContent.trim();
        if (text) textSpan.setAttribute("data-text", text);
      }
      return trigger;
    }

    const themed = buildThemeButton(label, href || undefined);
    const extraClasses = Array.from(trigger.classList).filter(
      (name) =>
        name &&
        name !== "pointer-events-none" &&
        !String(name).startsWith("is-")
    );
    if (extraClasses.length) {
      themed.className = Array.from(
        new Set([...themed.className.split(/\s+/).filter(Boolean), ...extraClasses])
      ).join(" ");
    }
    if (href) {
      themed.setAttribute("href", href);
      if (trigger.getAttribute("action")) {
        themed.setAttribute("action", trigger.getAttribute("action"));
      }
    }
    themed.setAttribute(LOAD_MORE_ATTR, "");
    themed.removeAttribute("mode");
    themed.removeAttribute("aria-busy");
    themed.classList.remove("pointer-events-none");
    ["aria-label", "role", "tabindex"].forEach((name) => {
      const value = trigger.getAttribute(name);
      if (value != null) themed.setAttribute(name, value);
    });
    trigger.replaceWith(themed);
    return themed;
  };

  const THEME_LOADER_SELECTORS = [
    ".loading-overlay",
    "[data-loading-overlay]",
    ".loading__spinner",
    ".collection__loading",
    "[class*='loading-spinner']",
    "[class*='spinner']",
    ".loader",
  ];

  let themeLoader = null;
  let themeLoaderState = null;

  const getThemeLoader = () => {
    if (themeLoader && document.contains(themeLoader)) return themeLoader;
    const scope = getSectionHost() || document;
    for (const selector of THEME_LOADER_SELECTORS) {
      const found = scope.querySelector(selector);
      if (found) {
        themeLoader = found;
        return found;
      }
    }
    themeLoader = null;
    return null;
  };

  const setThemeLoader = (busy) => {
    const loader = getThemeLoader();
    if (!loader) return false;
    if (busy) {
      if (!themeLoaderState) {
        themeLoaderState = {
          hidden: loader.hasAttribute("hidden"),
          classHidden: loader.classList.contains("hidden"),
          display: loader.style.display,
        };
      }
      loader.removeAttribute("hidden");
      loader.classList.remove("hidden");
      loader.style.display = "";
      return true;
    }
    if (themeLoaderState) {
      if (themeLoaderState.hidden) loader.setAttribute("hidden", "");
      if (themeLoaderState.classHidden) loader.classList.add("hidden");
      loader.style.display = themeLoaderState.display;
      themeLoaderState = null;
    }
    return true;
  };

  const clearThemeLoader = () => {
    themeLoader = null;
    themeLoaderState = null;
  };

  const setLoading = (busy) => {
    if (!setThemeLoader(busy)) {
      const host = getSectionHost();
      if (host) host.classList.toggle("is-cf-loading", Boolean(busy));
    }
    const section = getSectionElement();
    if (section) {
      section.setAttribute("aria-busy", busy ? "true" : "false");
    }
  };

  const getFacetForm = () =>
    document.querySelector("#FacetFiltersContainer form[is='facet-form']") ||
    document.querySelector("form[is='facet-form'][data-apply-on-submit]") ||
    document.querySelector("form[is='facet-form']");

  let renderInFlight = null;

  const handleCollectionRendered = () => {
    clearThemeLoader();
    refresh();
    pruneBrandParam();
    searchState.active = false;
    searchState.originalChildren = null;
    searchState.originalHTML = null;
    searchState.restorePrefetch = null;
    applyComingSoonState();
    resyncSearch();
    ensureLoadMoreButton(getLoadMoreTrigger());
    observeLoadMore();
    observePromoDuplicates();
    dropDuplicatePromos();
    // Re-capture clean snapshot after section swap
    setTimeout(() => {
      const g = getSearchGrid();
      if (g && !searchState.active) {
        searchState.originalHTML = captureCleanSnapshot(g);
        searchState.originalChildren = captureCleanChildren(g);
      }
    }, 300);
  };

  const renderCollection = async (url) => {
    const sectionId = getSectionId();
    const cleanUrl = new URL(url.toString(), location.origin);
    cleanUrl.searchParams.delete("section_id");
    const form = USE_THEME_RENDERER ? getFacetForm() : null;
    if (form && typeof form.renderSection === "function") {
      form.dirty = true;
      form.renderSection(cleanUrl.toString());
      return true;
    }
    if (renderInFlight) return renderInFlight;
    setLoading(true);
    renderInFlight = (async () => {
      try {
        const html = await fetchSectionHtml(cleanUrl, sectionId);
        const doc = parseHtml(html);
        if (!swapSection(sectionId, doc)) {
          throw new Error("Section markup not found in the response");
        }
        window.history.pushState({}, "", cleanUrl.toString());
        document.dispatchEvent(new CustomEvent("collection:reloaded"));
        return true;
      } catch (error) {
        console.warn(
          "[Inspired by] AJAX update failed, falling back to a page load:",
          error
        );
        window.location.assign(cleanUrl.toString());
        return false;
      } finally {
        renderInFlight = null;
        setLoading(false);
      }
    })();
    return renderInFlight;
  };

  const COMING_SOON_CLASS = "cf-coming-soon-state";

  const buildClearAllLink = () => {
    const link = buildThemeButton("Clear all", getClearSelectionUrl().toString());
    link.setAttribute("data-cf-clear-filters", "");
    link.classList.add("cf-clear-all");
    return link;
  };

  const buildComingSoonState = () => {
    const wrapper = document.createElement("div");
    wrapper.className =
      `empty-state grid items-center justify-center text-center gap-7d5 ${COMING_SOON_CLASS}`;
    const heading = document.createElement("h2");
    heading.className = "h2";
    heading.textContent = "COMING SOON";
    wrapper.append(heading, buildClearAllLink());
    return wrapper;
  };

  const selectionShowsComingSoon = (selection) => {
    if (!selection || !selection.comingSoon.length) return false;
    if (COMING_SOON_POLICY === "all") {
      return selection.comingSoon.length >= selection.brands.length;
    }
    return true;
  };

  const applyComingSoonState = () => {
    const container = getProductGridContainer();
    if (!container) return;
    const panel = getFilterPanel(getFilterWrap());
    const active = panel
      ? selectionShowsComingSoon(collectSelection(panel))
      : false;
    const applied = container.getAttribute("data-cf-coming-soon") === "1";
    const grid = container.querySelector("motion-list, .card-grid");
    const pagination = container.querySelector(".pagination, [class*='pagination']");
    const serverEmptyState = container.querySelector(
      `.empty-state:not(.${COMING_SOON_CLASS})`
    );
    const searchIsFiltering = searchState.tokens.length > 0;
    let block = container.querySelector(`.${COMING_SOON_CLASS}`);

    if (active) {
      if (!serverEmptyState) {
        if (!block) {
          block = buildComingSoonState();
          container.appendChild(block);
        }
        block.style.display = "";
      }
      if (grid) {
        if (!grid.hasAttribute("data-cf-prev-display")) {
          grid.setAttribute(
            "data-cf-prev-display",
            grid.style.display || ""
          );
        }
        grid.style.display = "none";
      }
      if (pagination) {
        if (!pagination.hasAttribute("data-cf-prev-display")) {
          pagination.setAttribute(
            "data-cf-prev-display",
            pagination.style.display || ""
          );
        }
        pagination.style.display = "none";
      }
      container.setAttribute("data-cf-coming-soon", "1");
      return;
    }

    if (block) block.style.display = "none";
    if (applied) {
      if (grid) {
        grid.style.display = grid.getAttribute("data-cf-prev-display") || "";
        grid.removeAttribute("data-cf-prev-display");
      }
      if (pagination) {
        pagination.style.display = searchIsFiltering
          ? "none"
          : pagination.getAttribute("data-cf-prev-display") || "";
        pagination.removeAttribute("data-cf-prev-display");
      }
      container.removeAttribute("data-cf-coming-soon");
    }
  };

  const brandsMatch = (a, b) =>
    normalizeText(a) === normalizeText(b);

  const syncInputState = (panel, paramName) => {
    const url = new URL(location.href);
    const selectedValues = url.searchParams.getAll(paramName);
    const selectedBrands = url.searchParams.getAll("inspired_brands");

    getInputs(panel).forEach((input) => {
      const matchingValues = getMatchingValues(input);
      const brand = getBrand(input);

      if (selectedBrands.length) {
        input.checked = selectedBrands.some((selected) =>
          brandsMatch(selected, brand)
        );
        return;
      }
      if (input.getAttribute("data-has-values") === "false") {
        input.checked = false;
        return;
      }
      if (matchingValues.length) {
        input.checked = matchingValues.some((value) =>
          selectedValues.includes(value)
        );
        return;
      }
      input.checked =
        !input.hasAttribute("data-cf-brand") &&
        selectedValues.includes(input.value);
    });
  };

  const createInspirationPill = (brand) => {
    const pill = document.createElement("div");
    pill.className =
      "removable-facet flex items-center justify-between gap-3 " +
      "lg:gap-4 text-xs lg:text-sm leading-none custom-inspiration-pill";
    const title = document.createElement("div");
    title.className = "facet-title flex items-center gap-2";
    title.textContent = brand;
    const removeButton = document.createElement("button");
    removeButton.type = "button";
    removeButton.className =
      "block relative remove-custom-inspiration";
    removeButton.setAttribute("data-brand", brand);
    removeButton.setAttribute(
      "aria-label",
      `Remove filter "${brand}"`
    );
    removeButton.style.cssText =
      "background:none;border:none;padding:0;cursor:pointer;";
    removeButton.innerHTML = `
      <span class="btn-text">
        <svg
          class="icon icon-remove icon-xs"
          viewBox="0 0 10 10"
          stroke="currentColor"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          role="presentation"
          style="width:10px;height:10px;"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="m1 9 8-8M1 1l8 8"
          />
        </svg>
      </span>
    `;
    pill.append(title, removeButton);
    return pill;
  };

  const updateActiveFacets = (panel, paramName, checkedInputs) => {
    const activeFacets = getActiveFacetContainer();
    if (!activeFacets) return;
    activeFacets
      .querySelectorAll(".custom-inspiration-pill")
      .forEach((pill) => pill.remove());
    const selectedValues = new Set();
    checkedInputs.forEach((input) => {
      getMatchingValues(input).forEach((value) => {
        selectedValues.add(normalizeText(value));
      });
    });
    if (selectedValues.size) {
      activeFacets
        .querySelectorAll(".removable-facet")
        .forEach((facet) => {
          if (facet.classList.contains("custom-inspiration-pill")) {
            return;
          }
          let shouldHide = false;
          const removeLink = facet.querySelector(
            'a[is="facet-remove"]'
          );
          if (removeLink?.getAttribute("href")) {
            const url = new URL(
              removeLink.getAttribute("href"),
              location.origin
            );
            const removedValues = getRemovedValues(
              url,
              paramName
            );
            shouldHide = removedValues.some((value) =>
              selectedValues.has(normalizeText(value))
            );
          }
          if (!shouldHide) {
            const title = facet.querySelector(".facet-title");
            if (title) {
              shouldHide = selectedValues.has(
                normalizeText(title.textContent)
              );
            }
          }
          if (shouldHide) {
            facet.style.display = "none";
            facet.setAttribute("data-cf-pill-hidden", "1");
          } else if (
            facet.getAttribute("data-cf-pill-hidden")
          ) {
            facet.style.display = "";
            facet.removeAttribute("data-cf-pill-hidden");
          }
        });
    }
    checkedInputs.forEach((input) => {
      activeFacets.appendChild(
        createInspirationPill(getBrand(input))
      );
    });
  };

  const pruneBrandParam = () => {
    const panel = getFilterPanel(getFilterWrap());
    if (!panel || !(panel.querySelector("input[data-inspired-option]") )) return;
    const checked = getInputs(panel)
      .filter((input) => input.checked)
      .map((input) => getBrand(input))
      .filter(Boolean);
    const url = new URL(location.href);
    const current = url.searchParams.getAll("inspired_brands");
    if (current.join("|") === checked.join("|")) return;
    url.searchParams.delete("inspired_brands");
    checked.forEach((brand) => url.searchParams.append("inspired_brands", brand));
    window.history.replaceState(
      window.history.state,
      "",
      url.pathname + url.search + url.hash
    );
  };

  const refresh = () => {
    const wrap = getFilterWrap();
    if (!wrap) return;
    const button = wrap.querySelector(".inspired-by-filter-button");
    const panel = getFilterPanel(wrap);
    const paramName = getInspiredParam(panel);
    if (!button || !panel || !paramName) return;
    syncInputState(panel, paramName);
    const themedButton =
      ensureThemedButton(button) || button;
    ensureThemedButton(wrap.querySelector(".apply-inspiredby-filters-btn"));
    const checkedInputs = getInputs(panel).filter(
      (input) => input.checked
    );
    themedButton.classList.toggle(
      "active",
      checkedInputs.length > 0
    );
    updateActiveFacets(
      panel,
      paramName,
      checkedInputs
    );
  };

  const SEARCH_INPUT_SELECTOR = "[data-collection-search-input]";
  const SEARCH_CLEAR_SELECTOR = "[data-collection-search-clear]";
  const SEARCH_EMPTY_CLASS = "local-search-empty-state";
  const SEARCH_EMPTY_TITLE_ATTR = "data-search-empty-title";
  const SEARCH_DEBOUNCE = 300;
  const SEARCH_INDEX_CONCURRENCY = 4;
  const SEARCH_INDEX_CACHE_LIMIT = 12;

  const searchState = {
    perform: null,
    input: null,
    query: "",
    tokens: [],
    timer: null,
    requestId: 0,
    active: false,
    originalChildren: null,
    originalHTML: null,
    restorePrefetch: null, // { key, promise } clean grid HTML downloaded in advance
    preSearchPaginationDisplay: "",
    indexBuilding: null,
    indexBuildingKey: null,
    useServerRestore: true, // v3: use server HTML on clear to avoid half-translate
  };

  const collectionIndexes = new Map();
  const collectionInspirationIndexes = new Map();

  const entryKeyOf = (card) => {
    const link = card.querySelector('a[href*="/products/"]');
    const href = link?.getAttribute("href");
    return href || card.outerHTML;
  };

  const getSearchInput = () => document.querySelector(SEARCH_INPUT_SELECTOR);
  const getSearchClearButton = () => document.querySelector(SEARCH_CLEAR_SELECTOR);

  const getSearchGrid = () => {
    const container = getProductGridContainer();
    return container ? container.querySelector("motion-list, .card-grid") : null;
  };

  // Clean any animation transforms that motion-list / theme adds
  const cleanMotionStyles = (root) => {
    if (!root) return;
    root.style?.removeProperty?.('transform');
    const els = root.querySelectorAll ? root.querySelectorAll('[style]') : [];
    els.forEach(el => {
      if (!el.style) return;
      // Remove only the properties that cause half-shift
      if (el.style.transform) el.style.transform = '';
      // Keep our own opacity handling, but clear animation opacity if needed
      // Don't blanket remove opacity on images, only on cards
      if (el.classList && (el.classList.contains('product-card') || el.classList.contains('card') || el.classList.contains('product-card-updated'))) {
        el.style.removeProperty('transform');
        el.style.removeProperty('opacity');
      }
    });
    // Also clean grid itself
    if (root.style) {
      root.style.removeProperty('transform');
    }
  };

  const captureCleanSnapshot = (grid) => {
    if (!grid) return "";
    const temp = document.createElement('div');
    temp.innerHTML = grid.innerHTML;
    temp.querySelectorAll('[style]').forEach(el => {
      el.style.removeProperty('transform');
      if (el.classList.contains('product-card') || el.classList.contains('card') || el.classList.contains('product-card-updated')) {
        el.style.removeProperty('opacity');
      }
      if (el.getAttribute('style') === '') el.removeAttribute('style');
    });
    return temp.innerHTML;
  };

  const captureCleanChildren = (grid) => {
    if (!grid) return [];
    return Array.from(grid.children).map(el => {
      const clone = el.cloneNode(true);
      clone.style.removeProperty('transform');
      clone.style.removeProperty('opacity');
      clone.querySelectorAll('[style]').forEach(inner => {
        inner.style.removeProperty('transform');
        if (inner.classList.contains('product-card') || inner.classList.contains('card') || inner.classList.contains('product-card-updated')) {
          inner.style.removeProperty('opacity');
        }
        if (inner.getAttribute('style') === '') inner.removeAttribute('style');
      });
      if (clone.getAttribute('style') === '') clone.removeAttribute('style');
      return clone;
    });
  };

  const searchTokensOf = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/['''`]/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim()
      .split(" ")
      .filter(Boolean);

  const isSearchPromo = (element) => {
    if (!element || element.nodeType !== 1) return false;
    return (
      element.classList.contains("product-card--promo") ||
      element.matches("[class*='promo'], [class*='promotion']") ||
      Boolean(element.querySelector("[class*='promo'], [class*='promotion']"))
    );
  };

  const getProductTitle = (element) => {
    const titleElement = element.matches("[data-product-title]")
      ? element
      : element.querySelector("[data-product-title]");
    if (titleElement?.getAttribute("data-product-title")) {
      return titleElement.getAttribute("data-product-title");
    }
    const fallbackTitle = element.querySelector(".card__heading, .product-card__title, .heading");
    return fallbackTitle?.textContent || "";
  };

  const searchCardCache = new WeakMap();

  const getSearchCardMeta = (card) => {
    if (!searchCardCache.has(card)) {
      searchCardCache.set(card, {
        promo: isSearchPromo(card),
        haystack: searchTokensOf(getProductTitle(card)).join(" "),
      });
    }
    return searchCardCache.get(card);
  };

  const setSearchValue = (value, { focus = false } = {}) => {
    const input = getSearchInput();
    if (input) {
      input.value = value;
      if (focus) input.focus();
    }
    return input;
  };

  const setSearchBusy = (busy) => {
    getSearchInput()
      ?.closest(".collection-name-search")
      ?.classList.toggle("is-searching", Boolean(busy));
  };

  const updateSearchEmptyState = (grid, label, shouldShow) => {
    let emptyState = grid.querySelector(`.${SEARCH_EMPTY_CLASS}`);
    if (!shouldShow) {
      if (emptyState) emptyState.remove();
      return;
    }
    if (!emptyState) {
      emptyState = document.createElement("div");
      emptyState.className =
        `${SEARCH_EMPTY_CLASS} col-span-full py-12 px-4 flex flex-col items-center justify-center text-center gap-3 w-full`;
      const title = document.createElement("p");
      title.className = "text-xl font-medium tracking-tight";
      title.setAttribute(SEARCH_EMPTY_TITLE_ATTR, "");
      const description = document.createElement("p");
      description.className = "text-sm opacity-60";
      description.textContent = "Check your spelling or try using another term";
      const resetButton = document.createElement("button");
      resetButton.type = "button";
      resetButton.className = "button button--sm mt-3 reset-search-btn";
      resetButton.textContent = "Clear Search";
      resetButton.style.cssText = "cursor:pointer;padding:8px 16px;";
      resetButton.addEventListener("click", () => {
        setSearchValue("", { focus: true });
        performSearch();
      });
      emptyState.append(title, description, resetButton);
      grid.appendChild(emptyState);
    }
    const title = emptyState.querySelector(`[${SEARCH_EMPTY_TITLE_ATTR}]`);
    if (title) title.textContent = `No products match your search "${label}"`;
  };

  const getCollectionIndexKey = () => {
    const url = new URL(location.href);
    const paramName = getInspiredParam(getFilterPanel(getFilterWrap()));
    if (paramName) url.searchParams.delete(paramName);
    return productListQuery(url);
  };

  const getCollectionMeta = (container = getProductGridContainer()) => {
    const metaEl = container?.querySelector("[data-collection-meta]");
    if (metaEl) {
      try {
        const parsed = JSON.parse(metaEl.textContent);
        return {
          total: parseInt(parsed.total, 10) || 0,
          perPage: parseInt(parsed.perPage, 10) || 0,
        };
      } catch {
        /* fall through */
      }
    }
    return { total: 0, perPage: 0 };
  };

  const getPageInspirationIndex = (container) => {
    const indexEl = container?.querySelector("[data-cf-page-inspiration-index]");
    if (!indexEl) return [];
    try {
      const parsed = JSON.parse(indexEl.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const buildCardEntry = (card) => ({
    key: entryKeyOf(card),
    haystack: getSearchCardMeta(card).haystack,
    node: card,
  });

  const fetchCollectionIndexPage = async (pageNumber) => {
    const sectionId = getSectionId();
    const url = new URL(location.href);
    url.searchParams.delete("section_id");
    const paramName = getInspiredParam(getFilterPanel(getFilterWrap()));
    if (paramName) url.searchParams.delete(paramName);
    url.searchParams.set("page", String(pageNumber));
    const html = await fetchSectionHtml(url, sectionId);
    const doc = parseHtml(html);
    const container = doc.getElementById("ProductGridContainer");
    const grid = container?.querySelector("motion-list, .card-grid");
    const inspirationEntries = getPageInspirationIndex(container).map((entry) => ({
      key: String(entry.productId || ""),
      inspiration: entry.inspiration,
    }));
    if (!grid) {
      return {
        entries: [],
        inspirationEntries,
        meta: getCollectionMeta(container),
      };
    }
    const entries = collectProductCards(grid).map((card) => {
      const adopted = document.adoptNode(card);
      return buildCardEntry(adopted);
    });
    return { entries, inspirationEntries, meta: getCollectionMeta(container) };
  };

  const fetchCollectionIndexPageWithRetry = async (pageNumber) => {
    let lastError;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        return await fetchCollectionIndexPage(pageNumber);
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  };

  const buildCollectionIndex = () => {
    const key = getCollectionIndexKey();
    const cached = collectionIndexes.get(key);
    if (cached) return Promise.resolve(cached);
    if (searchState.indexBuilding && searchState.indexBuildingKey === key) {
      return searchState.indexBuilding;
    }

    searchState.indexBuildingKey = key;
    const building = (async () => {
      try {
        const firstPage = await fetchCollectionIndexPageWithRetry(1);
        const { total, perPage } = firstPage.meta;
        const pageCount = perPage && total ? Math.ceil(total / perPage) : 1;
        const results = new Array(pageCount);
        const inspirationResults = new Array(pageCount);
        results[0] = firstPage.entries;
        inspirationResults[0] = firstPage.inspirationEntries;
        const failedPages = [];
        let cursor = 2;
        const worker = async () => {
          while (cursor <= pageCount) {
            const page = cursor++;
            try {
              const response = await fetchCollectionIndexPageWithRetry(page);
              results[page - 1] = response.entries;
              inspirationResults[page - 1] = response.inspirationEntries;
            } catch (error) {
              console.warn("[Collection search] Failed to index page", page, error);
              results[page - 1] = [];
              inspirationResults[page - 1] = [];
              failedPages.push(page);
            }
          }
        };
        await Promise.all(
          Array.from(
            { length: Math.min(SEARCH_INDEX_CONCURRENCY, pageCount - 1) },
            worker
          )
        );
        if (failedPages.length) {
          throw new Error(`Could not index collection page(s): ${failedPages.join(", ")}`);
        }

        const seen = new Set();
        const products = [];
        results.flat().forEach((entry) => {
          if (!entry || seen.has(entry.key)) return;
          seen.add(entry.key);
          products.push(entry);
        });
        const seenInspiration = new Set();
        const inspirationProducts = [];
        inspirationResults.flat().forEach((entry) => {
          if (!entry || !entry.key || seenInspiration.has(entry.key)) return;
          seenInspiration.add(entry.key);
          inspirationProducts.push(entry);
        });

        if (collectionIndexes.size >= SEARCH_INDEX_CACHE_LIMIT) {
          const oldestKey = collectionIndexes.keys().next().value;
          collectionIndexes.delete(oldestKey);
          collectionInspirationIndexes.delete(oldestKey);
        }
        collectionIndexes.set(key, products);
        collectionInspirationIndexes.set(key, inspirationProducts);
        return products;
      } finally {
        searchState.indexBuilding = null;
        searchState.indexBuildingKey = null;
      }
    })();
    searchState.indexBuilding = building;
    return building;
  };

  const normalizeInspiration = (value) =>
    String(value == null ? "" : value)
      .toLowerCase()
      .replace(/[\u2018\u2019\u02bc`\u00b4']/g, "")
      .replace(/&/g, " and ")
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const stripInspirationCredit = (value) =>
    normalizeInspiration(value)
      .replace(/^(?:inspired|inspiried|inspird|inpsired|insired|inpired)\s*by\s*/, "")
      .trim();

  const parseInspirationBrand = (raw) => {
    const entry = String(raw == null ? "" : raw).trim();
    if (!entry) return null;
    const aliases = [];
    const parenthetical = /\(([^)]*)\)/g;
    let match;
    while ((match = parenthetical.exec(entry)) !== null) {
      match[1].split(/[|/]/).forEach((term) => {
        if (term.trim()) aliases.push(stripInspirationCredit(term));
      });
    }
    const parts = entry.split("|");
    if (parts.length > 1) {
      parts.slice(1).forEach((term) => {
        if (term.trim()) aliases.push(stripInspirationCredit(term));
      });
    }
    return {
      phrase: stripInspirationCredit(entry),
      aliases: Array.from(new Set(aliases.filter(Boolean))),
    };
  };

  const matchesInspirationBrand = (inspiration, brand) => {
    const raw = stripInspirationCredit(inspiration);
    const matcher = parseInspirationBrand(brand);
    if (!raw || !matcher) return false;
    const padded = ` ${raw} `;
    return [matcher.phrase, ...matcher.aliases].some(
      (phrase) => phrase && padded.includes(` ${phrase} `)
    );
  };

  const getMatchingInspirationValues = (entries, brands) => {
    const values = [];
    const seen = new Set();

    entries.forEach((entry) => {
      const inspirationValues = Array.isArray(entry.inspiration)
        ? entry.inspiration
        : [entry.inspiration];
      const productInspiration = inspirationValues.filter(Boolean);

      productInspiration.forEach((value) => {
        const exactValue = String(value);
        if (
          !seen.has(exactValue) &&
          brands.some((brand) => matchesInspirationBrand(exactValue, brand))
        ) {
          seen.add(exactValue);
          values.push(exactValue);
        }
      });
    });

    return values;
  };

  const updateInspirationAvailability = (panel, entries) => {
    getInputs(panel)
      .filter((input) => input.checked)
      .forEach((input) => {
        const hasValues =
          getMatchingInspirationValues(entries, [getBrand(input)]).length > 0;
        input.setAttribute("data-cf-brand", "");
        input.toggleAttribute("data-cf-coming-soon", !hasValues);
      });
  };

  // ===== FIXED RESTORE LOGIC v3 - fix half-translated images =====
  const hardResetProductCards = (grid) => {
    if (!grid) return;
    // Reset the grid itself
    grid.style.removeProperty('transform');
    grid.style.removeProperty('opacity');
    grid.style.removeProperty('transition');
    grid.style.opacity = '1';
    grid.style.pointerEvents = '';

    // Reset every product card and its inner media/carousel
    grid.querySelectorAll('.product-card, .product-card-updated, .card, motion-list, .card-grid').forEach(el => {
      el.style.removeProperty('transform');
    });

    grid.querySelectorAll('secondary-media, .product-card__carousel, .product-card__secondary, .media, .media--height, img, .product-card__media').forEach(el => {
      el.style.removeProperty('transform');
      el.style.removeProperty('translate');
      if (el.tagName === 'SECONDARY-MEDIA') {
        el.setAttribute('selected-index', '0');
        // If theme's custom element has internal track, reset it
        const track = el.shadowRoot ? el.shadowRoot.querySelector('*') : null;
        if (track) track.style.transform = '';
      }
    });

    // Ensure images are not left at 50%
    grid.querySelectorAll('img').forEach(img => {
      img.style.transform = '';
      if (img.complete) {
        img.classList.remove('lazy-image--loading');
        img.classList.add('lazy-image--loaded');
      }
    });
  };

  // Clean grid HTML for the current filters, downloaded in advance when a search starts
  // so that clearing the search can restore it without waiting for the network.
  const getRestoreUrl = () => {
    const url = new URL(location.href);
    url.searchParams.delete('section_id');
    url.searchParams.delete('page');
    return url;
  };

  const prefetchRestoreHtml = () => {
    const url = getRestoreUrl();
    const key = url.toString();
    if (searchState.restorePrefetch && searchState.restorePrefetch.key === key) return;
    searchState.restorePrefetch = {
      key,
      promise: fetchSectionHtml(url, getSectionId()).catch(() => null),
    };
  };

  const restoreViaServer = async (grid) => {
    try {
      const sectionId = getSectionId();
      const url = getRestoreUrl();
      // Keep current filters but not search (search is client-only)
      const prefetched = searchState.restorePrefetch;
      searchState.restorePrefetch = null;
      let html = prefetched && prefetched.key === url.toString() ? await prefetched.promise : null;
      if (!html) html = await fetchSectionHtml(url, sectionId);
      const doc = parseHtml(html);
      const freshContainer = doc.getElementById('ProductGridContainer');
      const freshGrid = freshContainer?.querySelector('motion-list, .card-grid');
      if (freshGrid && freshGrid.innerHTML.trim().length > 50) {
        grid.innerHTML = freshGrid.innerHTML;
        // Clean again after server HTML
        hardResetProductCards(grid);
        if (typeof grid.reload === 'function') {
          // Delay reload until layout is stable
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              try { grid.reload(); } catch(e){}
              hardResetProductCards(grid);
            });
          });
        }
        return true;
      }
    } catch (e) {
      console.warn('[Collection search] server restore failed', e);
    }
    return false;
  };

  const restoreOriginalGrid = async () => {
    const container = getProductGridContainer();
    const grid = getSearchGrid();
    if (!grid) return;

    let restored = false;

    // Try server restore first - guarantees 100% clean HTML without any animation state
    // This fixes the half-translated image bug
    if (searchState.useServerRestore) {
      restored = await restoreViaServer(grid);
    }

    if (!restored) {
      if (searchState.originalHTML) {
        grid.innerHTML = searchState.originalHTML;
      } else if (searchState.originalChildren) {
        const clones = searchState.originalChildren.map(el => {
          const c = el.cloneNode(true);
          c.style.removeProperty('transform');
          c.style.removeProperty('opacity');
          return c;
        });
        grid.replaceChildren(...clones);
      }
      hardResetProductCards(grid);
      if (typeof grid.reload === 'function') {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            try { grid.reload(); } catch(e){}
            hardResetProductCards(grid);
          });
        });
      }
    }

    const pagination = container?.querySelector(".pagination, [class*='pagination']");
    if (pagination) {
      pagination.style.display = searchState.preSearchPaginationDisplay || "";
      if (pagination.getAttribute("data-cf-prev-display")) {
        pagination.style.display = pagination.getAttribute("data-cf-prev-display") || "";
        pagination.removeAttribute("data-cf-prev-display");
      }
    }
    searchState.preSearchPaginationDisplay = "";

    try {
      grid.dispatchEvent(new CustomEvent("grid:reloaded", {bubbles: true}));
      document.dispatchEvent(new CustomEvent("collection:search:restored", {detail:{grid}}));
    } catch(e) {}

    ensureLoadMoreButton(getLoadMoreTrigger());
    observeLoadMore();
    dropDuplicatePromos();
  };

  const performSearch = async () => {
    const input = getSearchInput();
    const clearButton = getSearchClearButton();
    const container = getProductGridContainer();
    const grid = getSearchGrid();
    if (!input || !grid) return;

    const label = input.value.trim();
    const tokens = searchTokensOf(label);
    const requestId = ++searchState.requestId;

    searchState.input = input;
    searchState.query = input.value;
    searchState.tokens = tokens;
    if (clearButton) clearButton.hidden = label === "";

    const pagination = container?.querySelector(".pagination, [class*='pagination']");

    // Establish CSS transition styling once on the grid
    if (!grid.style.transition) {
      grid.style.transition = "opacity 0.15s ease-in-out";
    }

    if (tokens.length === 0) {
      if (searchState.active) {
        searchState.active = false;
        // v3: async restore - may fetch server HTML
        restoreOriginalGrid().then(() => {
          updateSearchEmptyState(grid, label, false);
          applyComingSoonState();
        });
        grid.style.opacity = "1";
        grid.style.pointerEvents = "";
        updateSearchEmptyState(grid, label, false);
        applyComingSoonState();
      } else {
        grid.style.opacity = "1";
        grid.style.pointerEvents = "";
        updateSearchEmptyState(grid, label, false);
        applyComingSoonState();
      }
      return;
    }

    if (!searchState.active) {
      searchState.active = true;
      prefetchRestoreHtml();
      // FIX v2: Save clean snapshot without animation transforms
      // Only capture if not already captured at init (init captures cleanest)
      if (!searchState.originalHTML) {
        searchState.originalHTML = captureCleanSnapshot(grid);
        searchState.originalChildren = captureCleanChildren(grid);
      } else {
        // Ensure we have children array for indexing
        if (!searchState.originalChildren) {
          searchState.originalChildren = captureCleanChildren(grid);
        }
      }
      searchState.preSearchPaginationDisplay = pagination ? pagination.style.display : "";
    }
    if (pagination) pagination.style.display = "none";

    // QUICK LOADING ANIMATION
    grid.style.opacity = "0.3";
    setSearchBusy(true);

    let products;
    try {
      products = await buildCollectionIndex();
    } catch (error) {
      console.warn("[Collection search] Index build failed:", error);
      products = (searchState.originalChildren || [])
        .filter((el) => el.nodeType === 1 && !isSearchPromoBlock(el))
        .map(buildCardEntry);
    }
    if (requestId !== searchState.requestId) return;
    setSearchBusy(false);

    const matches = products.filter((entry) =>
      tokens.every((token) => entry.haystack.includes(token))
    );

    // FIX v3: Clone and clean transforms - prevent half-translate in search results too
    const fragment = document.createDocumentFragment();
    matches.forEach(entry => {
      try {
        const clone = entry.node.cloneNode(true);
        clone.style.removeProperty('transform');
        clone.style.removeProperty('opacity');
        clone.querySelectorAll('secondary-media, .product-card__carousel, .media, img').forEach(inner => {
          inner.style.removeProperty('transform');
          if (inner.tagName === 'SECONDARY-MEDIA') inner.setAttribute('selected-index','0');
        });
        fragment.appendChild(clone);
      } catch(e) {
        const c = entry.node.cloneNode(true);
        c.style.removeProperty('transform');
        fragment.appendChild(c);
      }
    });
    grid.replaceChildren(fragment);
    hardResetProductCards(grid);
    if (typeof grid.reload === "function") {
      try { grid.reload(); } catch(e){}
    }

    // RESTORE ANIMATION
    grid.style.opacity = "1";
    grid.style.pointerEvents = "";

    updateSearchEmptyState(grid, label, matches.length === 0);
    applyComingSoonState();
  };

  const resyncSearch = () => {
    const input = getSearchInput();
    if (input && searchState.query && input.value === "") {
      input.value = searchState.query;
    }
    // Only perform if search was active before
    if (searchState.tokens.length) {
      performSearch();
    }
  };

  const initCollectionSearch = () => {
    if (searchInitialized) return;
    searchInitialized = true;
    document.addEventListener("input", (event) => {
      const input = event.target?.closest?.(SEARCH_INPUT_SELECTOR);
      if (!input) return;
      clearTimeout(searchState.timer);
      searchState.timer = setTimeout(() => {
        searchState.timer = null;
        performSearch();
      }, SEARCH_DEBOUNCE);
    });
    document.addEventListener("keydown", (event) => {
      const input = event.target?.closest?.(SEARCH_INPUT_SELECTOR);
      if (!input) return;
      if (event.key === "Escape") {
        clearTimeout(searchState.timer);
        searchState.timer = null;
        setSearchValue("");
        performSearch();
        input.blur();
      }
      if (event.key === "Enter") {
        clearTimeout(searchState.timer);
        searchState.timer = null;
        performSearch();
      }
    });
    document.addEventListener("click", (event) => {
      const button = event.target?.closest?.(SEARCH_CLEAR_SELECTOR);
      if (!button) return;
      event.preventDefault();
      clearTimeout(searchState.timer);
      searchState.timer = null;
      setSearchValue("", { focus: true });
      performSearch();
    });
    searchState.perform = performSearch;
    // Don't auto-perform on init if input empty - avoids overwriting originalHTML
    if (getSearchInput()?.value?.trim()) {
      performSearch();
    }
  };

  const getPaginationContainer = () => {
    const container = getProductGridContainer();
    return container
      ? container.querySelector(".pagination, [class*='pagination']")
      : null;
  };

  const getLoadMoreTrigger = () => {
    const pagination = getPaginationContainer();
    if (!pagination) return null;
    return (
      pagination.querySelector(`[${LOAD_MORE_ATTR}]`) ||
      pagination.querySelector("a[href*='page='], button, a[href], [role='button'], .button") ||
      null
    );
  };

  const triggerLooksLikeLoadMore = (trigger) => {
    if (!trigger) return false;
    if (trigger.hasAttribute(LOAD_MORE_ATTR)) return true;
    if (/show more|load more|view more/i.test(trigger.textContent || "")) {
      return true;
    }
    if (trigger.tagName === "BUTTON") return true;
    return /[?&]page=/.test(trigger.getAttribute("href") || "");
  };

  let firstSeenPaginationMode = null;

  const getPaginationMode = () => {
    const declared = getCollectionEl()?.getAttribute("data-pagination-mode");
    if (declared) return declared;
    if (firstSeenPaginationMode) return firstSeenPaginationMode;
    const trigger = getLoadMoreTrigger();
    return trigger?.getAttribute("mode") || "";
  };

  const isAppendPagination = () => {
    const mode = getCollectionEl()?.getAttribute("data-pagination-mode");
    if (mode) return APPEND_PAGINATION_MODES.includes(mode);
    const pagination = getPaginationContainer();
    const triggers = pagination
      ? pagination.querySelectorAll("a[href], button")
      : [];
    return triggers.length === 1 && triggerLooksLikeLoadMore(triggers[0]);
  };

  const nextPageUrlFrom = (trigger, base = location.href) => {
    const declared =
      trigger?.getAttribute?.("action") ||
      trigger?.getAttribute?.("href") ||
      trigger?.getAttribute?.("data-url") ||
      trigger?.getAttribute?.("data-href");
    if (declared) {
      try {
        return new URL(declared, base);
      } catch {
      }
    }
    const url = new URL(location.href);
    const current = parseInt(url.searchParams.get("page") || "1", 10) || 1;
    url.searchParams.set("page", String(current + 1));
    return url;
  };

  const isSearchPromoBlock = (element) =>
    isSearchPromo(element) || element.classList.contains(SEARCH_EMPTY_CLASS);

  const collectProductCards = (gridEl) => {
    const candidates = Array.from(
      gridEl.querySelectorAll(
        ".card, product-card, .product-card, [data-product-title]"
      )
    ).filter((element) => !isSearchPromoBlock(element));
    return candidates.filter(
      (element) =>
        !candidates.some(
          (other) => other !== element && other.contains(element)
        )
    );
  };

  const promoFingerprint = (promo) =>
    `${promo.className}|${promo.innerHTML.replace(/\s+/g, " ").trim()}`;

  let promoGuardObserver = null;

  const dropDuplicatePromos = () => {
    if (!REMOVE_DUPLICATE_PROMOS) return;
    const container = getProductGridContainer();
    if (!container) return;
    const seen = new Set();
    container.querySelectorAll(".product-card--promo").forEach((promo) => {
      const fingerprint = promoFingerprint(promo);
      if (seen.has(fingerprint)) {
        promo.remove();
        return;
      }
      seen.add(fingerprint);
    });
  };

  const observePromoDuplicates = () => {
    if (!REMOVE_DUPLICATE_PROMOS || promoGuardObserver) return;
    if (typeof MutationObserver !== "function") return;
    const container = getProductGridContainer();
    if (!container) return;
    let queued = false;
    promoGuardObserver = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      Promise.resolve().then(() => {
        queued = false;
        dropDuplicatePromos();
      });
    });
    promoGuardObserver.observe(container, { childList: true, subtree: true });
  };

  let loadMoreInFlight = false;
  let loadMoreObserver = null;

  const loadMoreProducts = async () => {
    if (loadMoreInFlight) return false;
    const container = getProductGridContainer();
    const grid = container?.querySelector("motion-list, .card-grid");
    const pagination = getPaginationContainer();
    const trigger = getLoadMoreTrigger();
    if (!container || !grid || !pagination || !trigger) return false;
    if (trigger.hasAttribute("aria-busy")) return false;
    const sectionId = getSectionId();
    const pageUrl = nextPageUrlFrom(trigger);
    loadMoreInFlight = true;
    trigger.classList.add("pointer-events-none");
    trigger.setAttribute("aria-busy", "true");
    try {
      const html = await fetchSectionHtml(pageUrl, sectionId);
      const doc = parseHtml(html);
      const freshContainer = doc.getElementById("ProductGridContainer");
      const freshGrid = freshContainer?.querySelector("motion-list, .card-grid");
      const freshPagination = freshContainer?.querySelector(
        ".pagination, [class*='pagination']"
      );
      if (!freshGrid) {
        throw new Error("No product grid in the pagination response");
      }
      const incoming = collectProductCards(freshGrid);
      const fragment = document.createDocumentFragment();
      incoming.forEach((card) => fragment.appendChild(document.adoptNode(card)));
      grid.appendChild(fragment);
      if (typeof grid.reload === "function") grid.reload();
      if (freshPagination) {
        const template = document.createElement("template");
        template.innerHTML = freshPagination.innerHTML;
        if (TAKE_OVER_INFINITE_AUTOLOAD) {
          template.content
            .querySelectorAll('[mode="infinite"]')
            .forEach((node) => node.removeAttribute("mode"));
        }
        template.content
          .querySelectorAll('[is="infinite-button"]')
          .forEach((node) => {
            node.removeAttribute("is");
            node.removeAttribute("mode");
          });
        pagination.replaceChildren(
          document.adoptNode(template.content)
        );
        const freshTrigger = getLoadMoreTrigger();
        if (freshTrigger) {
          ensureLoadMoreButton(freshTrigger);
        } else {
          pagination.remove();
        }
      } else {
        pagination.remove();
      }
      window.history.replaceState({}, "", pageUrl.toString());
      applyComingSoonState();
      // If search is active, re-index to include newly loaded products
      if (searchState.tokens.length) {
        collectionIndexes.clear();
        performSearch();
      }
      observeLoadMore();
      return true;
    } catch (error) {
      console.warn(
        "[CF] Show more failed, falling back to a page load:",
        error
      );
      window.location.assign(pageUrl.toString());
      return false;
    } finally {
      loadMoreInFlight = false;
      const current = getLoadMoreTrigger();
      if (current) {
        current.classList.remove("pointer-events-none");
        current.removeAttribute("aria-busy");
      } else {
        trigger.classList.remove("pointer-events-none");
        trigger.removeAttribute("aria-busy");
      }
    }
  };

  const observeLoadMore = () => {
    if (loadMoreObserver) {
      loadMoreObserver.disconnect();
      loadMoreObserver = null;
    }
    if (!AUTO_LOAD_INFINITE) return;
    if (getPaginationMode() !== "infinite") return;
    if (typeof IntersectionObserver !== "function") return;
    const trigger = getLoadMoreTrigger();
    if (!trigger) return;
    loadMoreObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        if (searchState.tokens.length) return;
        loadMoreProducts();
      },
      { rootMargin: "400px 0px" }
    );
    loadMoreObserver.observe(trigger);
  };

  const handleLoadMoreClick = (event) => {
    const target =
      event.target?.nodeType === 1
        ? event.target
        : event.target?.parentElement;
    if (!target) return;
    const pagination = getPaginationContainer();
    if (!pagination || !pagination.contains(target)) return;
    if (!isAppendPagination()) return;
    const trigger = getLoadMoreTrigger();
    if (!trigger || !trigger.contains(target)) return;
    event.preventDefault();
    if (trigger.getAttribute("is") === "infinite-button") {
      event.stopPropagation();
    }
    loadMoreProducts();
  };

  const handleClick = (event) => {
    const target =
      event.target?.nodeType === 1
        ? event.target
        : event.target?.parentElement;
    if (!target) return;
    if (
      target.closest('[is="facet-remove"]') &&
      typeof customElements !== "undefined" &&
      customElements.get("facet-remove")
    ) {
      return;
    }
    const wrap = getFilterWrap();
    const panel = getFilterPanel(wrap);
    const filterButton = target.closest(
      ".inspired-by-filter-button"
    );
    const applyButton = target.closest(
      ".apply-inspiredby-filters-btn"
    );
    const removeButton = target.closest(
      ".remove-custom-inspiration"
    );
    const clearComingSoon = target.closest("[data-cf-clear-filters]");
    const facetRemoveButton = target.closest(
      '#FacetFiltersActive a[is="facet-remove"], ' +
      '[data-active-facets] a[is="facet-remove"]'
    );

    if (clearComingSoon) {
      event.preventDefault();
      if (panel) {
        getInputs(panel).forEach((input) => {
          input.checked = false;
        });
      }
      renderCollection(getClearSelectionUrl());
      return;
    }

    if (filterButton) {
      event.preventDefault();
      event.stopPropagation();
      if (panel) {
        panel.classList.toggle("active");
      }
      return;
    }

    if (applyButton) {
      event.preventDefault();
      if (panel) {
        panel.classList.remove("active");
        const applyValues = (values) => {
          const url = buildUrlFromSelection(panel, values);
          if (productListQuery(url) === productListQuery(location.href)) {
            window.history.pushState({}, "", url.toString());
            refresh();
            applyComingSoonState();
            document.dispatchEvent(new CustomEvent("collection:reloaded"));
          } else {
            renderCollection(url);
          }
        };
        const selection = collectSelection(panel);
        if (!selection.brands.length) {
          applyValues([]);
        } else {
          const indexKey = getCollectionIndexKey();
          buildCollectionIndex()
            .then(() => {
              const entries = collectionInspirationIndexes.get(indexKey) || [];
              updateInspirationAvailability(panel, entries);
              applyValues(getMatchingInspirationValues(entries, selection.brands));
            })
            .catch((error) => {
              console.warn("[Inspired by] Could not index collection products:", error);
              applyValues(selection.values);
            });
        }
      }
      return;
    }

    if (removeButton) {
      event.preventDefault();
      if (panel) {
        const brand = removeButton.getAttribute("data-brand");
        getInputs(panel).forEach((input) => {
          if (brandsMatch(getBrand(input), brand)) {
            input.checked = false;
          }
        });
        const url = buildUrlFromSelection(panel);
        if (productListQuery(url) === productListQuery(location.href)) {
          window.history.pushState({}, "", url.toString());
          refresh();
          applyComingSoonState();
        } else {
          renderCollection(url);
        }
      }
      return;
    }

    if (
      panel?.classList.contains("active") &&
      wrap &&
      !wrap.contains(target)
    ) {
      panel.classList.remove("active");
    }

    if (facetRemoveButton) {
      window.setTimeout(refresh, 150);
    }
  };

  const claimInfiniteAutoload = () => {
    if (!TAKE_OVER_INFINITE_AUTOLOAD) return;
    const trigger = getLoadMoreTrigger();
    if (!firstSeenPaginationMode) {
      firstSeenPaginationMode =
        trigger?.getAttribute("mode") ||
        getCollectionEl()?.getAttribute("data-pagination-mode") ||
        null;
    }
    if (firstSeenPaginationMode !== "infinite") return;
    if (trigger?.getAttribute("mode")) trigger.removeAttribute("mode");
    document
      .querySelectorAll(".pagination [mode='infinite'], [is='infinite-button'][mode='infinite']")
      .forEach((node) => node.removeAttribute("mode"));
  };

  claimInfiniteAutoload();

  const init = () => {
    if (initialized) return;
    initialized = true;
    claimInfiniteAutoload();
    // Capture clean snapshot BEFORE motion-list animates - this is the fix for half-translate
    const grid = getSearchGrid();
    if (grid && !searchState.originalHTML) {
      // Use rAF to capture after initial paint but before heavy animation?
      // Actually capture immediately and clean transforms
      searchState.originalHTML = captureCleanSnapshot(grid);
      searchState.originalChildren = captureCleanChildren(grid);
    }
    refresh();
    applyComingSoonState();
    ensureLoadMoreButton(getLoadMoreTrigger());
    initCollectionSearch();
    buildCollectionIndex().catch((error) => {
      console.warn("[Collection filter] Background index build failed:", error);
    });
    observeLoadMore();
    observePromoDuplicates();
    
    // Re-capture after a short delay to ensure we have the pristine server HTML
    // without any motion-list inline styles
    setTimeout(() => {
      const g = getSearchGrid();
      if (g) {
        const clean = captureCleanSnapshot(g);
        if (clean && clean.length > 100) {
          // Only overwrite if current snapshot looks like it had transforms
          if (!searchState.originalHTML || searchState.originalHTML.includes('transform')) {
            searchState.originalHTML = clean;
            searchState.originalChildren = captureCleanChildren(g);
          }
        }
      }
    }, 500);
  };

  document.addEventListener("click", handleLoadMoreClick, true);
  document.addEventListener("click", handleClick);
  document.addEventListener("collection:reloaded", handleCollectionRendered);
  document.addEventListener(
    "cf:inspired-ready",
    refresh
  );

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, {
      once: true,
    });
  } else {
    init();
  }

  window.CFCollectionUI = {
    refresh,
    collectSelection,
    buildUrlFromSelection,
    valuesRemoved: getRemovedValues,
    normalizeText,
    getInputs: () =>
      getInputs(getFilterPanel(getFilterWrap())),
    renderCollection,
    productListQuery,
    getSectionId,
    applyComingSoonState,
    selectionShowsComingSoon,
    loadMore: loadMoreProducts,
    dropDuplicatePromos,
    isAppendPagination,
    ensureThemedButton,
    ensureLoadMoreButton,
    themeButtonTraits: getThemeButtonTraits,
    search: () => (searchState.perform ? searchState.perform() : null),
    searchState,
    setSearch: (value) => {
      setSearchValue(value);
      performSearch();
    },
  };
})();
